import { useEffect, useState } from "react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { sendTrackingEvent } from "../../lib/tracking";
import { useTranslations } from "../../i18n";

export type TrackingData = {
  cartId: string;
  currency: string;
  value: number;
  items: {
    item_id: string;
    item_name: string;
    item_category?: string;
    item_category2?: string;
    item_variant?: string;
    price: number;
    quantity: number;
  }[];
};

type StripeElementsFormProps = {
  stripePublishableKey: string;
  clientSecret: string;
  bookingIds: string[];
  isRequestBooking: boolean;
  trackingData?: TrackingData;
  onSuccess: (bookingIds: string[]) => void;
  onError: (error: Error) => void;
};

function PaymentForm({
  bookingIds,
  isRequestBooking,
  trackingData,
  onSuccess,
  onError,
}: {
  bookingIds: string[];
  isRequestBooking: boolean;
  trackingData?: TrackingData;
  onSuccess: (bookingIds: string[]) => void;
  onError: (error: Error) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { t } = useTranslations();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setErrorMessage(t.payment.stripeNotLoaded);
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Soumettre d'abord les éléments pour validation
      const { error: submitError } = await elements.submit();
      if (submitError) {
        console.error("Erreur submit:", submitError);
        setErrorMessage(submitError.message || t.payment.errorOccurred);
        setIsProcessing(false);
        return;
      }

      // Tracking add_payment_info (informations de paiement validées)
      if (trackingData) {
        sendTrackingEvent("add_payment_info", null, {
          cart_id: trackingData.cartId,
          currency: trackingData.currency,
          value: trackingData.value,
          items: trackingData.items,
        });
      }

      // Confirmer le PaymentIntent (avec capture manuelle pour REQUEST, auto pour INSTANT)
      const { error: confirmError, paymentIntent } =
        await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: "if_required",
        });

      if (confirmError) {
        console.error("Erreur confirmation Payment Intent:", confirmError);
        setErrorMessage(
          confirmError.message || t.payment.errorPayment,
        );
        onError(new Error(confirmError.message || "Payment failed"));
      } else if (paymentIntent) {
        console.log(
          "PaymentIntent confirmé:",
          paymentIntent.id,
          "status:",
          paymentIntent.status,
        );

        if (paymentIntent.status === "requires_capture") {
          // Réservation REQUEST : fonds autorisés, attente validation prestataire
          console.log(
            "Autorisation réussie - Fonds bloqués en attente de capture",
          );
          onSuccess(bookingIds);
        } else if (paymentIntent.status === "succeeded") {
          // Paiement INSTANT : paiement complètement traité
          onSuccess(bookingIds);
        } else {
          console.log("Statut PaymentIntent:", paymentIntent.status);
          setErrorMessage(
            t.payment.unexpectedPaymentStatus.replace("{status}", paymentIntent.status),
          );
        }
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Unknown error");
      setErrorMessage(err.message);
      onError(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />

      {errorMessage && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full rounded-lg bg-brand-600 px-4 py-3 font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isProcessing
          ? t.common.processing
          : isRequestBooking
            ? t.payment.authorizePayment
            : t.payment.payNow}
      </button>
    </form>
  );
}

export function StripeElementsForm({
  stripePublishableKey,
  clientSecret,
  bookingIds,
  isRequestBooking,
  trackingData,
  onSuccess,
  onError,
}: StripeElementsFormProps) {
  const { t } = useTranslations();
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    if (stripePublishableKey) {
      setStripePromise(loadStripe(stripePublishableKey));
    }
  }, [stripePublishableKey]);

  if (!stripePromise || !clientSecret) {
    return (
      <div className="p-4 text-center text-gray-600">
        {t.cart.paymentFormLoading}
      </div>
    );
  }

  const brandColor =
    getComputedStyle(document.documentElement)
      .getPropertyValue("--main-color")
      .trim() || "#7c3aed";

  const options = {
    clientSecret,
    appearance: {
      theme: "stripe" as const,
      variables: {
        colorPrimary: brandColor,
      },
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentForm
        bookingIds={bookingIds}
        isRequestBooking={isRequestBooking}
        trackingData={trackingData}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
}
